import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { CancelToken } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import { formatDate } from 'frontend-components/lib/helpers';
import UploaderButtons from './UploaderButtons';
import ErrorImage from '../Icons/ErrorImage';
import {
  VIDEO,
  MAXIMUM_IMAGE_SIZE,
  MAXIMUM_VIDEO_SIZE,
  MAXIMUM_FILES_COUNT,
  MAXIMUM_FILES_COUNT_AT_ONCE,
  SUPPORTED_FILE_TYPES,
  PHOTO,
} from '../../constants/photo';
import {
  uploadPhoto,
  dataURLtoFile,
  getFileName,
} from '../../helpers/uploadPhoto';

import {
  StyledFilesUploader,
  StyledWebcamWrapper,
  StyledWebcam,
  StyledError,
} from './FilesUploader.styles';
import useDeviceOrientation from '../../hooks/useDeviceOrientation';

const FilesUploader = ({
  allFiles,
  reportFiles,
  uploadedFiles,
  handleAddFile,
  disabled,
  clientName,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const orientation = useDeviceOrientation();
  const webcamRef = useRef(null);
  const { online } = useSelector((state) => state.offline);

  const [takingPhotoDisabled, setTakingPhotoDisabled] = useState(false);
  const [isButtonActive, setButtonActive] = useState(true);
  const [recordError, setRecordError] = useState('');

  const isValidNumber = (files) => {
    if (reportFiles + files.length > MAXIMUM_FILES_COUNT) {
      enqueueSnackbar(t('report_pic_limit', { number: MAXIMUM_FILES_COUNT }), {
        variant: 'error',
        autoHideDuration: null,
      });
      return files.slice(0, MAXIMUM_FILES_COUNT - reportFiles);
    }

    if (uploadedFiles + files.length > MAXIMUM_FILES_COUNT_AT_ONCE) {
      enqueueSnackbar(
        t('report_pic_limit_at_once', { number: MAXIMUM_FILES_COUNT_AT_ONCE }),
        {
          variant: 'error',
        }
      );
      return files.slice(0, MAXIMUM_FILES_COUNT_AT_ONCE - uploadedFiles);
    }
    return files;
  };

  const isValidType = ({ type }) => {
    const isValid = Object.keys(SUPPORTED_FILE_TYPES).includes(type);

    if (!isValid) {
      enqueueSnackbar(
        `${
          type.includes('video')
            ? t('validation_video_type')
            : t('validation_app_pic_type')
        }: ${type}`,
        {
          variant: 'error',
        }
      );
    }
    return isValid;
  };

  const isValidSize = ({ size, type }, maxSize) => {
    const isValid = size < maxSize;
    if (!isValid) {
      enqueueSnackbar(
        type.includes('video')
          ? t('validation_video_size')
          : t('validation_app_pic_size'),
        { variant: 'error' }
      );
    }
    return isValid;
  };

  const isValidOnline = () => {
    if (!online) {
      enqueueSnackbar(t('validation_video_offline'), { variant: 'error' });
    }
    return online;
  };

  const isPhotoValid = (file) => {
    return isValidType(file) && isValidSize(file, MAXIMUM_IMAGE_SIZE);
  };

  const isVideoValid = (file) => {
    return (
      isValidType(file) &&
      isValidSize(file, MAXIMUM_VIDEO_SIZE) &&
      isValidOnline
    );
  };

  const getFileData = (file, type) => {
    const uploadDate = formatDate(new Date());

    return {
      id: uuidv4(),
      fileName: getFileName(uploadDate, file, clientName),
      createdAt: uploadDate,
      updatedAt: uploadDate,
      description: '',
      mediaType: type,
    };
  };

  const handleCapturePhoto = async () => {
    const screenshot = webcamRef.current.getScreenshot();
    const source = CancelToken.source();

    const data = getFileData(null, PHOTO);

    if (screenshot && !takingPhotoDisabled) {
      const file = dataURLtoFile(screenshot, data.fileName);
      data.link = await uploadPhoto(file);

      const newFile = new File([screenshot], data.fileName);

      handleAddFile([{ file: newFile, data, source }]);

      setTakingPhotoDisabled(true);

      setTimeout(() => {
        setTakingPhotoDisabled(false);
      }, 500);
    }
  };

  const handleSelectFile = async (e) => {
    const files = isValidNumber(Array.from(e.target.files));

    if (isEmpty(files)) {
      return;
    }

    const photos = files.filter((file) => file.type.includes('image'));
    const videos = files.filter((file) => file.type.includes('video'));

    if (videos.length > 0) {
      videos.forEach((file) => {
        if (!isVideoValid(file)) {
          return;
        }

        const source = CancelToken.source();
        const data = getFileData(file, VIDEO);

        data.link = URL.createObjectURL(file);

        const newFile = new File([file], data.fileName);

        handleAddFile([{ file: newFile, data, source }]);
      });
    }

    await Promise.all(
      [...photos].map(async (file) => {
        if (!isPhotoValid(file)) {
          return;
        }

        const source = CancelToken.source();
        const data = getFileData(file, PHOTO);

        data.link = await uploadPhoto(file);

        const newFile = new File([data.link], data.fileName);

        handleAddFile([{ file: newFile, data, source }]);
      })
    );
  };

  useEffect(() => {
    if (allFiles.length >= MAXIMUM_FILES_COUNT_AT_ONCE) {
      enqueueSnackbar(
        t('report_pic_limit_at_once', { number: MAXIMUM_FILES_COUNT_AT_ONCE }),
        {
          variant: 'error',
        }
      );
      setButtonActive(false);
    } else if (reportFiles >= MAXIMUM_FILES_COUNT) {
      enqueueSnackbar(t('report_pic_limit', { number: MAXIMUM_FILES_COUNT }), {
        variant: 'error',
        autoHideDuration: null,
      });
      setButtonActive(false);
    } else {
      setButtonActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFiles, reportFiles]);

  return (
    <StyledFilesUploader>
      <StyledWebcamWrapper>
        {!recordError && (
          <StyledWebcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            onUserMediaError={(error) => {
              setRecordError(isString(error) ? error : error.message);
            }}
            videoConstraints={{
              facingMode: 'environment',
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              aspectRatio: orientation === 'landscape' ? 16 / 9 : 9 / 16,
            }}
            forceScreenshotSourceSize
            screenshotQuality={1}
          />
        )}
        {recordError && (
          <StyledError>
            <ErrorImage />
            {recordError}
          </StyledError>
        )}
      </StyledWebcamWrapper>
      <UploaderButtons
        handleCapturePhoto={handleCapturePhoto}
        handleSelectFile={handleSelectFile}
        photoDisabled={!!recordError || !isButtonActive || disabled}
        galleryDisabled={!isButtonActive || disabled}
      />
    </StyledFilesUploader>
  );
};

FilesUploader.propTypes = {
  allFiles: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
  ),
  reportFiles: PropTypes.number,
  uploadedFiles: PropTypes.number,
  handleAddFile: PropTypes.func,
  disabled: PropTypes.bool,
  clientName: PropTypes.string,
};

export default FilesUploader;
